// src/components/Project/ProjectUserManagement.tsx
import React, { useState, useEffect } from 'react';
import { getRoleDisplayName } from '../../utils/roleDisplayNames';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';
import {
  PersonAdd,
  Delete,
  Warning,
  ExpandMore,
  Email,
  CalendarToday,
} from '@mui/icons-material';
import {
  getProjectUsers,
  removeUserFromProject,
  getIncompleteActions,
  ProjectUser,
  ProjectUserApiError,
  IncompleteAction,
} from '../../services/projectUserApi';
import InviteUserDialog from './InviteUserDialog';
import { 
  getPendingInvitations, 
  resendInvitation, 
  resetInvitationExpiration,
  Invitation,
  InvitationApiError 
} from '../../services/invitationApi';
import { format } from 'date-fns';

interface ProjectUserManagementProps {
  projectId: number;
  onUsersUpdated?: () => void; // Callback to notify parent when users are added/removed/invited
}

const ProjectUserManagement: React.FC<ProjectUserManagementProps> = ({ projectId, onUsersUpdated }) => {
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);
  const [removeConfirmDialog, setRemoveConfirmDialog] = useState<{
    open: boolean;
    user: ProjectUser | null;
    incompleteActions: IncompleteAction[];
    canRemove: boolean;
    willUnassign: boolean;
  }>({
    open: false,
    user: null,
    incompleteActions: [],
    canRemove: true,
    willUnassign: false,
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Invitations state
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [invitationActionLoading, setInvitationActionLoading] = useState<string | null>(null);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [resendInvitationId, setResendInvitationId] = useState<string | null>(null);
  const [expirationDialogOpen, setExpirationDialogOpen] = useState(false);
  const [expirationInvitationId, setExpirationInvitationId] = useState<string | null>(null);
  const [newExpirationDate, setNewExpirationDate] = useState<string>('');

  const loadProjectUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getProjectUsers(projectId);
      console.log('ProjectUserManagement: Loaded project users:', response);
      console.log('ProjectUserManagement: Number of users:', response.users?.length);
      console.log('ProjectUserManagement: Sample user data:', response.users?.[0]);
      setProjectUsers(response.users || []);
    } catch (err) {
      const apiError = err as ProjectUserApiError;
      console.error('ProjectUserManagement: Error loading project users:', err);
      setError(apiError.message || 'Failed to load project users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadProjectUsers();
      loadPendingInvitations();
    }
  }, [projectId]);

  const loadPendingInvitations = async () => {
    if (!projectId) return;
    setLoadingInvitations(true);
    try {
      const response = await getPendingInvitations(projectId);
      setPendingInvitations(response.invitations);
    } catch (err) {
      console.error('Error loading pending invitations:', err);
      // Don't show error to user, just log it
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      setInvitationActionLoading(invitationId);
      await resendInvitation(invitationId, { extendExpiration: true });
      setSuccessMessage('Invitation resent successfully');
      await loadPendingInvitations();
      setResendDialogOpen(false);
      setResendInvitationId(null);
    } catch (err) {
      const apiError = err as InvitationApiError;
      setError(apiError.message || 'Failed to resend invitation');
    } finally {
      setInvitationActionLoading(null);
    }
  };

  const handleResetExpiration = async () => {
    if (!expirationInvitationId || !newExpirationDate) return;
    
    try {
      setInvitationActionLoading(expirationInvitationId);
      await resetInvitationExpiration(expirationInvitationId, newExpirationDate);
      setSuccessMessage('Invitation expiration updated successfully');
      await loadPendingInvitations();
      setExpirationDialogOpen(false);
      setExpirationInvitationId(null);
      setNewExpirationDate('');
    } catch (err) {
      const apiError = err as InvitationApiError;
      setError(apiError.message || 'Failed to update expiration');
    } finally {
      setInvitationActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const handleInviteSuccess = () => {
    loadProjectUsers();
    // Notify parent to refresh its user list
    if (onUsersUpdated) {
      onUsersUpdated();
    }
  };

  const handleRemoveClick = async (user: ProjectUser) => {
    try {
      const incompleteActionsResponse = await getIncompleteActions(projectId, user.userId);
      setRemoveConfirmDialog({
        open: true,
        user,
        incompleteActions: incompleteActionsResponse.actions || [],
        canRemove: incompleteActionsResponse.canRemove !== false,
        willUnassign: !!incompleteActionsResponse.willUnassign,
      });
    } catch (err) {
      const apiError = err as ProjectUserApiError;
      setError(apiError.message || 'Failed to check incomplete actions');
    }
  };

  const handleRemoveConfirm = async () => {
    if (!removeConfirmDialog.user) {
      return;
    }

    const userId = removeConfirmDialog.user.userId;
    setRemovingUserId(userId);
    setError(null);

    try {
      await removeUserFromProject(projectId, userId);
      await loadProjectUsers();
      // Notify parent to refresh its user list
      if (onUsersUpdated) {
        onUsersUpdated();
      }
      setRemoveConfirmDialog({ open: false, user: null, incompleteActions: [], canRemove: true, willUnassign: false });
    } catch (err) {
      const apiError = err as ProjectUserApiError;
      setError(apiError.message || 'Failed to remove user from project');
    } finally {
      setRemovingUserId(null);
    }
  };

  const getUserTypeColor = (userType: string | null) => {
    switch (userType) {
      case 'TENANT':
        return 'primary';
      case 'FRAPORT':
        return 'secondary';
      case 'CLIENT':
        return 'success';
      default:
        return 'default';
    }
  };

  const getUserTypeLabel = (userType: string | null) => {
    switch (userType) {
      case 'TENANT':
        return 'Tenant';
      case 'FRAPORT':
        return 'Fraport';
      case 'CLIENT':
        return 'Client';
      default:
        return 'N/A';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Project Users</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setInviteDialogOpen(true)}
        >
          Invite Users to Project
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>User Type</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projectUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No users assigned to this project
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                projectUsers
                  .filter((projectUser) => projectUser != null) // Only filter out null/undefined entries
                  .map((projectUser) => {
                    // Support both nested user object and flat properties
                    // Backend now returns base company/userType in nested user object (from Users table)
                    // and project-specific overrides as top-level fields
                    const displayName = projectUser.user?.displayName || projectUser.userName || null;
                    const email = projectUser.user?.email || projectUser.userEmail || null;
                    // Priority: base values from nested user object > project-specific overrides > legacy flat properties
                    const company = projectUser.user?.company ?? projectUser.company ?? projectUser.userCompany ?? null;
                    const userType = projectUser.user?.userType ?? projectUser.userType ?? projectUser.userUserType ?? null;
                    const role = projectUser.user?.role || projectUser.userRole || null;
                    
                    return (
                      <TableRow key={projectUser.projectUserId}>
                        <TableCell>
                          {displayName || email || `User ID: ${projectUser.userId}`}
                        </TableCell>
                        <TableCell>{email || 'N/A'}</TableCell>
                        <TableCell>{company || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={getUserTypeLabel(userType)}
                            size="small"
                            color={getUserTypeColor(userType) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          {role || 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove user from project">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveClick(projectUser)}
                              disabled={removingUserId === projectUser.userId}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pending Invitations Accordion */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Pending Invitations ({pendingInvitations.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {loadingInvitations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : pendingInvitations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pending invitations for this project
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Expiration Date</TableCell>
                    <TableCell>Days Until Expiration</TableCell>
                    <TableCell>Invited By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingInvitations.map((invitation) => (
                    <TableRow key={invitation.invitationId}>
                      <TableCell>{invitation.email}</TableCell>
                      <TableCell>{invitation.roleName ? getRoleDisplayName(invitation.roleName) : ''}</TableCell>
                      <TableCell>{formatDate(invitation.expiresAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={invitation.isExpired ? 'Expired' : `${invitation.daysUntilExpiration} days`}
                          color={invitation.isExpired ? 'error' : invitation.daysUntilExpiration <= 3 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{invitation.invitedBy.displayName}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Resend Invitation">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setResendInvitationId(invitation.invitationId);
                              setResendDialogOpen(true);
                            }}
                            disabled={invitationActionLoading === invitation.invitationId}
                          >
                            <Email />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset Expiration">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setExpirationInvitationId(invitation.invitationId);
                              const currentDate = new Date(invitation.expiresAt);
                              currentDate.setDate(currentDate.getDate() + 7);
                              setNewExpirationDate(currentDate.toISOString().split('T')[0]);
                              setExpirationDialogOpen(true);
                            }}
                            disabled={invitationActionLoading === invitation.invitationId}
                          >
                            <CalendarToday />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
        projectId={projectId}
      />

      {/* Remove User Confirmation Dialog */}
      <Dialog
        open={removeConfirmDialog.open}
        onClose={() => setRemoveConfirmDialog({ open: false, user: null, incompleteActions: [], canRemove: true, willUnassign: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {!removeConfirmDialog.canRemove ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
              <Warning />
              <span>Cannot Remove User</span>
            </Box>
          ) : removeConfirmDialog.willUnassign ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
              <Warning />
              <span>Remove User and Unassign Actions</span>
            </Box>
          ) : (
            'Remove User from Project'
          )}
        </DialogTitle>
        <DialogContent>
          {removeConfirmDialog.user && (
            <>
              {!removeConfirmDialog.canRemove ? (
                <>
                  <DialogContentText sx={{ mb: 2 }}>
                    Cannot remove <strong>{removeConfirmDialog.user.user?.displayName || removeConfirmDialog.user.userName || removeConfirmDialog.user.user?.email || removeConfirmDialog.user.userEmail || 'this user'}</strong> from this execution project because they still have {removeConfirmDialog.incompleteActions.length} open action(s).
                    Reassign those actions before removing the user. Completed actions will keep this user as the user of record.
                  </DialogContentText>
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Open Actions:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                      {removeConfirmDialog.incompleteActions.map((action, index) => (
                        <Box component="li" key={index} sx={{ mb: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {action.actionType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Task: "{action.taskName}" • Phase: "{action.phaseName}"
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              ) : removeConfirmDialog.willUnassign ? (
                <>
                  <DialogContentText sx={{ mb: 2 }}>
                    Remove <strong>{removeConfirmDialog.user.user?.displayName || removeConfirmDialog.user.userName || removeConfirmDialog.user.user?.email || removeConfirmDialog.user.userEmail || 'this user'}</strong> from this planning project? They will be unassigned from {removeConfirmDialog.incompleteActions.length} action(s) and will no longer be selectable for new assignments.
                  </DialogContentText>
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Actions to unassign:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                      {removeConfirmDialog.incompleteActions.map((action, index) => (
                        <Box component="li" key={index} sx={{ mb: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {action.actionType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Task: "{action.taskName}" • Phase: "{action.phaseName}"
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              ) : (
                <DialogContentText>
                  Are you sure you want to remove <strong>{removeConfirmDialog.user.user?.displayName || removeConfirmDialog.user.userName || removeConfirmDialog.user.user?.email || removeConfirmDialog.user.userEmail || 'this user'}</strong> from this project? They will lose project access and will not be selectable for future assignments. Completed actions keep this user as the user of record.
                </DialogContentText>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveConfirmDialog({ open: false, user: null, incompleteActions: [], canRemove: true, willUnassign: false })}
            disabled={removingUserId !== null}
          >
            {removeConfirmDialog.canRemove ? 'Cancel' : 'Close'}
          </Button>
          {removeConfirmDialog.canRemove && (
            <Button
              onClick={handleRemoveConfirm}
              variant="contained"
              color="error"
              disabled={removingUserId !== null}
            >
              {removingUserId !== null ? 'Removing...' : (removeConfirmDialog.willUnassign ? 'Unassign and Remove' : 'Remove')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Resend Invitation Dialog */}
      <Dialog open={resendDialogOpen} onClose={() => {
        setResendDialogOpen(false);
        setResendInvitationId(null);
      }}>
        <DialogTitle>Resend Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to resend this invitation? The expiration date will be extended by 7 days.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setResendDialogOpen(false);
            setResendInvitationId(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={() => resendInvitationId && handleResendInvitation(resendInvitationId)}
            variant="contained"
            color="primary"
            disabled={invitationActionLoading === resendInvitationId}
          >
            {invitationActionLoading === resendInvitationId ? 'Sending...' : 'Resend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Expiration Dialog */}
      <Dialog open={expirationDialogOpen} onClose={() => {
        setExpirationDialogOpen(false);
        setExpirationInvitationId(null);
        setNewExpirationDate('');
      }}>
        <DialogTitle>Reset Expiration Date</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a new expiration date for this invitation.
          </DialogContentText>
          <TextField
            type="date"
            label="New Expiration Date"
            value={newExpirationDate}
            onChange={(e) => setNewExpirationDate(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setExpirationDialogOpen(false);
            setExpirationInvitationId(null);
            setNewExpirationDate('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleResetExpiration}
            variant="contained"
            color="primary"
            disabled={!newExpirationDate || invitationActionLoading === expirationInvitationId}
          >
            {invitationActionLoading === expirationInvitationId ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectUserManagement;

